import { useState } from 'react'
import { Sun, Moon, Menu, Search, ChevronDown, Settings } from 'lucide-react'
import { useTheme } from '@/contexts/ThemeContext'
import { useChat } from '@/contexts/ChatContext'
import { useAuth } from '@/contexts/AuthContext'
import { useOperator } from '@/contexts/OperatorContext'
import { EventSelectorModal } from './sidebar/EventSelectorModal'

interface HeaderProps {
  onToggleSidebar?: () => void
}

export function Header({ onToggleSidebar }: HeaderProps = {}) {
  const { theme, toggleTheme } = useTheme()
  const { isConnected } = useChat()
  const { claims, currentEventId, events, setCurrentEventId } = useAuth()
  const { short: brandName, logoUrl, company } = useOperator()
  const [eventModalOpen, setEventModalOpen] = useState(false)

  const activeEvent = events.find((e) => e.EventID === currentEventId)
  const eventLabel = activeEvent?.EventName ?? 'Select event'
  const initials = claims?.username?.[0]?.toUpperCase() ?? 'S'

  return (
    <>
      <header
        className="h-14 flex items-center gap-3 px-4 shrink-0"
        style={{ background: 'var(--brand-header)' }}
      >
        {onToggleSidebar && (
          <button
            type="button"
            onClick={onToggleSidebar}
            aria-label="Toggle sidebar"
            className="lg:hidden w-8 h-8 rounded-full flex items-center justify-center text-white/80 hover:bg-white/15 transition-colors"
          >
            <Menu className="w-4 h-4" />
          </button>
        )}

        {/* Logo + brand */}
        <div className="flex items-center gap-2.5 shrink-0">
          <img
            src={logoUrl}
            alt={company}
            className="h-9 w-auto rounded object-contain"
          />
          <span className="hidden sm:inline text-white font-bold text-[15px] tracking-tight"
            style={{ fontFamily: "'Rubik', 'Montserrat', sans-serif" }}
          >
            {brandName}
          </span>
        </div>

        {/* Event selector pill — opens modal */}
        <button
          type="button"
          onClick={() => setEventModalOpen(true)}
          className="flex items-center gap-1.5 rounded-lg px-3.5 py-1.5 text-white text-[13px] font-semibold transition-colors shrink-0 hover:bg-white/[0.22]"
          style={{
            background: 'rgba(255,255,255,0.13)',
            border: '1px solid rgba(255,255,255,0.2)',
            fontFamily: "'Rubik', 'Montserrat', sans-serif",
          }}
        >
          {isConnected && (
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 shrink-0" />
          )}
          <span className="truncate max-w-[200px]">{eventLabel}</span>
          <ChevronDown className="w-3.5 h-3.5 opacity-70 shrink-0" />
        </button>

        {/* Search placeholder */}
        <div
          className="hidden md:flex flex-1 max-w-[380px] items-center gap-2 rounded-lg px-3.5 py-[7px] text-white/50 text-[13px] cursor-pointer transition-colors hover:bg-white/[0.18]"
          style={{
            background: 'rgba(255,255,255,0.1)',
            border: '1px solid rgba(255,255,255,0.15)',
          }}
        >
          <Search className="w-3.5 h-3.5" />
          <span>Search events, plans, questions...</span>
          <kbd className="ml-auto text-[11px] px-1.5 py-0.5 rounded bg-white/15 font-mono">⌘K</kbd>
        </div>

        <div className="flex-1" />

        {/* Right actions */}
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={toggleTheme}
            aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
            className="w-[34px] h-[34px] rounded-full flex items-center justify-center text-white bg-white/10 hover:bg-white/[0.22] transition-colors"
          >
            {theme === 'light' ? <Moon className="w-[15px] h-[15px]" /> : <Sun className="w-[15px] h-[15px]" />}
          </button>

          <button
            type="button"
            className="w-[34px] h-[34px] rounded-full flex items-center justify-center text-white bg-white/10 hover:bg-white/[0.22] transition-colors"
            aria-label="Settings"
          >
            <Settings className="w-[15px] h-[15px]" />
          </button>

          {/* Avatar */}
          <div
            className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0"
            style={{
              background: 'rgba(255,255,255,0.15)',
              border: '2px solid rgba(255,255,255,0.3)',
            }}
          >
            {initials}
          </div>
        </div>
      </header>

      <EventSelectorModal
        open={eventModalOpen}
        onClose={() => setEventModalOpen(false)}
        onSelectEvent={setCurrentEventId}
        events={events}
        currentEventId={currentEventId}
      />
    </>
  )
}
